import { supabase } from "@/integrations/supabase/client";
import { trackAffiliateClick, trackAffiliateClickGA4 } from "@/lib/analytics";
import { getUtmColumns } from "@/hooks/useUtmPersistence";

let sessionId: string | null = null;
if (typeof window !== "undefined") {
  sessionId = sessionStorage.getItem("bhg_session");
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem("bhg_session", sessionId);
  }
}

// Phase 10a: in-memory vendor EPC cache. Populated lazily on the first
// affiliate click of a session via a single-row PostgREST lookup, then reused
// for every subsequent click on the same vendor without another network hop.
//
// We deliberately don't preload — most sessions only click 1-2 vendors so
// loading the full vendors table would be wasteful. Lazy lookup keeps the
// first click slightly slower but keeps the trackBuyClick fast path tight.
//
// Cache lives for the page lifetime (no TTL). EPC values change infrequently
// (admin tunes them per vendor) and a stale value just affects bidding-not
// attribution — small accuracy cost for big simplicity win.
const vendorEpcCache = new Map<string, number | null>();

async function getVendorEpc(vendorId: string | undefined): Promise<number | undefined> {
  if (!vendorId) return undefined;
  if (vendorEpcCache.has(vendorId)) {
    const v = vendorEpcCache.get(vendorId);
    return v == null ? undefined : v;
  }
  try {
    const { data } = await supabase
      .from("vendors")
      .select("epc_estimate")
      .eq("id", vendorId)
      .maybeSingle();
    const epc = (data as any)?.epc_estimate;
    const numeric = typeof epc === "number" ? epc : null;
    vendorEpcCache.set(vendorId, numeric);
    return numeric == null ? undefined : numeric;
  } catch {
    vendorEpcCache.set(vendorId, null);
    return undefined;
  }
}

export interface BuyClickParams {
  compoundId?: string;
  vendorId?: string;
  listingId?: string;
  compoundName?: string;
  vendorName?: string;
  /** Commission estimate (USD) — powers Meta VALUE-based bidding.
   *  Optional: if omitted, trackBuyClick auto-fills from vendors.epc_estimate. */
  estimatedValue?: number;
  /** Where on the site the click originated, e.g. "compound-price-block". The GA4 affiliate_click event's placement;
   *  without one it falls back to the page type (lib/pageCategory.ts). */
  source?: string;
}

export async function trackBuyClick(params: BuyClickParams) {
  // The GA4 affiliate_click event goes first and synchronously, while the click is still being handled: the document
  // listener in lib/analytics.ts then knows this click is covered, and a same-tab navigation cannot cut it off. Its
  // value is the caller's, or the vendor's EPC when an earlier click already looked it up.
  try {
    const cachedEpc = params.vendorId ? vendorEpcCache.get(params.vendorId) : undefined;
    trackAffiliateClickGA4({
      vendor: params.vendorName,
      compound: params.compoundName,
      placement: params.source,
      value: params.estimatedValue ?? (cachedEpc == null ? undefined : cachedEpc),
    });
  } catch {
    // Analytics must never block affiliate redirect.
  }

  // Resolve EPC value: caller-supplied estimatedValue wins, otherwise look up
  // the vendor's epc_estimate (cached in memory after first hit). The lookup
  // is awaited but capped — if it takes >300ms we fire without the value
  // rather than block the pixel/CAPI fires that downstream attribution depends on.
  let value: number | undefined = params.estimatedValue;
  if (value == null && params.vendorId) {
    try {
      value = await Promise.race<number | undefined>([
        getVendorEpc(params.vendorId),
        new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 300)),
      ]);
    } catch {
      // ignore — fire without value
    }
  }

  // Fire the pixels and GA4's add_to_cart (and the Meta/Reddit CAPI mirrors via
  // analytics.ts) so the pixel record exists even if the visitor closes the
  // tab before the DB insert below returns. GA4's affiliate_click went above.
  try {
    trackAffiliateClick(
      {
        content_id: params.compoundId || params.listingId,
        content_name: params.compoundName,
        content_category: "affiliate",
        source: params.source,
        value,
        currency: "USD",
        vendor_id: params.vendorId,
        vendor_name: params.vendorName,
        ...getUtmColumns(),
      },
      { ga4: false },
    );
  } catch {
    // Analytics must never block affiliate redirect.
  }

  try {
    await supabase.from("click_events").insert({
      compound_id: params.compoundId || null,
      vendor_id: params.vendorId || null,
      listing_id: params.listingId || null,
      compound_name: params.compoundName || null,
      vendor_name: params.vendorName || null,
      session_id: sessionId,
    });
  } catch {
    // Silent fail - don't break UX for analytics
  }
}
